import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ChevronDown, ChevronRight, Sparkles, Loader2, ArrowDownLeft, ArrowUpRight, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const stripHtml = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText || div.textContent || "";
};

const EmailMessage = ({ message }) => {
  const [expanded, setExpanded] = useState(false);
  const [fullBody, setFullBody] = useState(null);
  const [loadingBody, setLoadingBody] = useState(false);

  const isOutbound = message.direction === "outbound";

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !fullBody) {
      // Fetch full body if not already loaded
      setLoadingBody(true);
      try {
        const res = await axios.get(`${API}/email/activities/${message.id}/body`);
        setFullBody(res.data);
      } catch (e) {
        setFullBody({ body_html: message.snippet || "(unable to load)", body_type: "text", source: "error" });
      } finally {
        setLoadingBody(false);
      }
    }
  };

  return (
    <div className="border-l-2 border-slate-200 hover:border-[#2E7AD1] transition-colors" data-testid={`email-message-${message.id}`}>
      <button
        type="button"
        onClick={handleExpand}
        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
      >
        <div className={`mt-1 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isOutbound ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-700"}`}>
          {isOutbound ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownLeft className="h-3.5 w-3.5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-0.5">
            <span className="font-medium text-slate-700">{isOutbound ? "To" : "From"}: {isOutbound ? message.to_email : message.from_email}</span>
            <span className="text-slate-300">•</span>
            <span>{formatDateTime(message.date)}</span>
          </div>
          {!expanded && (
            <p className="text-sm text-slate-600 truncate">{message.snippet || "(no preview)"}</p>
          )}
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400 mt-2" /> : <ChevronRight className="h-4 w-4 text-slate-400 mt-2" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 ml-9">
          {loadingBody ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading full message...
            </div>
          ) : fullBody?.body_type === "html" ? (
            <div
              className="prose prose-sm max-w-none text-sm text-slate-700 bg-slate-50 rounded p-3 border border-slate-100 overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: fullBody.body_html }}
            />
          ) : (
            <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-3 border border-slate-100">
              {fullBody?.body_html ? stripHtml(fullBody.body_html) : message.snippet}
            </div>
          )}
          {fullBody?.source === "snippet_only" || fullBody?.source === "fallback_snippet" ? (
            <p className="text-xs text-amber-600 mt-1">Full body not available — showing preview only. Re-sync emails to capture full content.</p>
          ) : null}
        </div>
      )}
    </div>
  );
};

const ThreadCard = ({ thread, onSummarize }) => {
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  const handleSummarize = async (e) => {
    e.stopPropagation();
    setSummarizing(true);
    try {
      const ids = thread.messages.map((m) => m.id);
      const res = await axios.post(`${API}/email/thread/summarize`, { activity_ids: ids });
      setSummary(res.data.summary);
      toast.success("Thread summarized");
    } catch (err) {
      console.error(err);
      toast.error("Failed to summarize thread");
    } finally {
      setSummarizing(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={`email-thread-${thread.conversation_id}`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-3 hover:bg-slate-50 flex items-center gap-3"
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-800 truncate">{thread.subject || "(No Subject)"}</p>
            <Badge variant="secondary" className="text-xs flex-shrink-0">{thread.message_count} msg{thread.message_count !== 1 ? "s" : ""}</Badge>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Last activity: {formatDate(thread.last_date)} • {thread.participants.length} participant{thread.participants.length !== 1 ? "s" : ""}
          </p>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
            <div className="text-xs text-slate-500">
              Participants: <span className="font-medium text-slate-700">{thread.participants.join(", ")}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSummarize}
              disabled={summarizing}
              className="h-7 text-xs"
              data-testid={`summarize-thread-${thread.conversation_id}`}
            >
              {summarizing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
              AI Summarize
            </Button>
          </div>
          {summary && (
            <div className="px-3 py-2 bg-violet-50 border-b border-violet-100">
              <p className="text-xs font-semibold text-violet-700 mb-1">AI Summary</p>
              <p className="text-sm text-violet-900 whitespace-pre-wrap">{summary}</p>
            </div>
          )}
          <div className="bg-white">
            {thread.messages.map((m) => <EmailMessage key={m.id} message={m} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export const EmailThreadsPanel = ({ leadId, companyName, title = "Email Conversations", description }) => {
  const [threads, setThreads] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    const fetchThreads = async () => {
      setLoading(true);
      try {
        const url = leadId
          ? `${API}/leads/${leadId}/email-threads`
          : `${API}/companies/${encodeURIComponent(companyName)}/email-threads`;
        const res = await axios.get(url);
        setThreads(res.data.threads || []);
        setTotalMessages(res.data.total_messages || 0);
        setDomains(res.data.domains || []);
      } catch (e) {
        console.error("Failed to fetch email threads:", e);
        setThreads([]);
      } finally {
        setLoading(false);
      }
    };
    if (leadId || companyName) fetchThreads();
  }, [leadId, companyName]);

  return (
    <Card data-testid="email-threads-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="h-5 w-5 text-slate-400" />
          {title}
          {totalMessages > 0 && <Badge variant="secondary" className="ml-1">{totalMessages}</Badge>}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
        {domains.length > 0 && (
          <p className="text-xs text-slate-500">Matched domains: {domains.join(", ")}</p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading conversations...
          </div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-400">
            <Inbox className="h-8 w-8 mb-2" />
            <p className="text-sm">No email correspondence found yet.</p>
            <p className="text-xs mt-1">Emails will appear here after the next sync.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="email-threads-list">
            {threads.map((t) => (
              <ThreadCard key={t.conversation_id} thread={t} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailThreadsPanel;
